/**
 * Worker Message Types - v1.0
 * Type definitions for Synapse worker-shell communication
 */

// ============================================================================
// Core Types
// ============================================================================

export type MessageType =
  // Health & Status
  | 'health:check'
  | 'status:get'
  // Tasks
  | 'tasks:list'
  | 'task:get'
  | 'task:submit'
  | 'task:retry'
  // Metrics
  | 'metrics:get'
  | 'metrics:llm'
  | 'metrics:cost'
  // Config
  | 'config:get'
  | 'config:update'
  | 'config:frontend'
  | 'config:synapse'
  // Control
  | 'control:pause'
  | 'control:resume'
  // Claude Config
  | 'claude:config:get'
  | 'claude:config:save'
  // Voice
  | 'voice:tasks:list'
  | 'voice:task:submit'
  // Files
  | 'file:upload'
  // Feedback
  | 'feedback:list'
  | 'feedback:submit'
  // Subscriptions
  | 'subscribe:tasks'
  | 'subscribe:task'
  | 'unsubscribe'
  // LightGrid
  | 'lightgrid:add'
  // Deploy
  | 'deploy:execute'
  // Internal
  | 'worker:init'
  | 'worker:shutdown'
  | 'worker:heartbeat';

export type ErrorCode =
  | 'NETWORK_ERROR'
  | 'TIMEOUT'
  | 'WORKER_UNAVAILABLE'
  | 'INVALID_MESSAGE'
  | 'API_ERROR'
  | 'WEBSOCKET_ERROR'
  | 'SUBSCRIPTION_ERROR'
  | 'UNKNOWN_ERROR';

export type WorkerStatus = 'initializing' | 'ready' | 'error' | 'terminated';

// ============================================================================
// Message Envelopes
// ============================================================================

export interface WorkerMessage<T = unknown> {
  id: string;
  version: '1.0';
  type: MessageType;
  payload: T;
  timestamp: number;
}

export interface WorkerResponse<T = unknown> {
  id: string;
  version: '1.0';
  type: 'response' | 'error' | 'event';
  payload: T;
  timestamp: number;
  duration?: number;
}

export interface WorkerError {
  code: ErrorCode;
  message: string;
  details?: unknown;
  retryable: boolean;
  originalError?: string;
}

// ============================================================================
// Request Payloads
// ============================================================================

export interface HealthCheckRequest {}

export interface StatusRequest {}

export interface TasksListRequest {
  limit?: number;
  status?: string;
}

export interface TaskGetRequest {
  taskId: string;
}

export interface TaskSubmitRequest {
  description: string;
  priority?: 'LOW' | 'NORMAL' | 'HIGH';
}

export interface TaskRetryRequest {
  taskId: string;
}

export interface MetricsRequest {
  type?: 'all' | 'llm' | 'cost';
}

export interface ConfigGetRequest {
  configType?: 'all' | 'frontend' | 'synapse';
}

export interface ConfigUpdateRequest {
  config: Record<string, unknown>;
}

export interface ClaudeConfigGetRequest {
  file: 'personality' | 'snippets' | 'memory';
}

export interface ClaudeConfigSaveRequest {
  file: 'personality' | 'snippets' | 'memory';
  content: string;
}

export interface VoiceTaskSubmitRequest {
  query: string;
  priority?: 'low' | 'normal' | 'high';
}

export interface FileUploadRequest {
  filename: string;
  destination: string;
  content: string;
  encoding?: 'base64' | 'utf-8';
}

export interface FeedbackSubmitRequest {
  project: string;
  message: string;
  email?: string;
}

export interface SubscribeTasksRequest {}

export interface SubscribeTaskRequest {
  taskId: string;
}

export interface UnsubscribeRequest {
  subscriptionId: string;
}

export interface LightGridAddRequest {
  componentId: string;
  config: Record<string, unknown>;
}

export interface DeployExecuteRequest {
  taskId: string;
  options?: Record<string, unknown>;
}

// ============================================================================
// Response Payloads
// ============================================================================

export interface HealthCheckResponse {
  status: 'healthy' | 'degraded' | 'unhealthy';
  version: string;
  monitor_running: boolean;
  timestamp: string;
}

export interface StatusResponse {
  pipeline_state: 'running' | 'paused' | 'error';
  active_tasks: number;
  queued_tasks: number;
  uptime_seconds: number;
}

export interface Task {
  id: string;
  task_id?: string;
  name?: string;
  description?: string;
  status: string;
  phase?: string;
  progress: number;
  priority?: string;
  timestamp?: number;
  created_at?: string;
  started_at?: string;
  completed_at?: string;
  result?: string;
  error?: string | null;
  output?: string;
  code_output?: string;
}

export interface TasksListResponse {
  tasks: Task[];
}

export interface TaskGetResponse {
  task: Task;
  output?: string;
  status_details?: TaskStatus;
}

export interface TaskStatus {
  task_id: string;
  status: string;
  progress: number;
  phase: string;
  tokens_used: number;
  lines_generated: number;
  output_preview: string;
  files_modified: string[];
  started_at: string | null;
  elapsed_seconds: number | null;
}

export interface TaskSubmitResponse {
  taskId: string;
  status: string;
}

export interface TaskRetryResponse {
  success: boolean;
  taskId: string;
}

export interface MetricsResponse {
  cpu_usage: number;
  memory_usage: number;
  tasks_completed_24h: number;
  api_calls_24h: number;
  tokens_used_24h: number;
  llm?: LLMMetrics;
  cost?: CostMetrics;
}

export interface LLMMetrics {
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
  model_usage: Record<string, number>;
}

export interface CostMetrics {
  total_cost: number;
  daily_cost: number;
  monthly_cost: number;
}

export interface ConfigResponse {
  config: Record<string, unknown>;
}

export interface ClaudeConfigResponse {
  content: string;
}

export interface VoiceTasksResponse {
  tasks: Task[];
}

export interface FileUploadResponse {
  success: boolean;
  path?: string;
}

export interface FeedbackListResponse {
  feedback: Array<{
    id: string;
    project: string;
    message: string;
    email?: string;
    created_at: string;
  }>;
}

export interface FeedbackSubmitResponse {
  success: boolean;
  id?: string;
}

export interface SubscriptionResponse {
  subscriptionId: string;
}

// ============================================================================
// Event Types (for subscriptions)
// ============================================================================

export type TaskEventType = 'created' | 'updated' | 'completed' | 'failed' | 'progress';

export interface TaskEvent {
  eventType: TaskEventType;
  task: Task;
  timestamp: number;
}

export interface TaskStreamEvent {
  type: 'log' | 'output' | 'error' | 'warning' | 'success';
  data: string;
  source?: string;
  timestamp: string;
}

// ============================================================================
// Type Guards
// ============================================================================

export function isWorkerError(payload: unknown): payload is WorkerError {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'code' in payload &&
    'message' in payload &&
    'retryable' in payload
  );
}

export function isTaskEvent(payload: unknown): payload is TaskEvent {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'eventType' in payload &&
    'task' in payload
  );
}

export function isTaskStreamEvent(payload: unknown): payload is TaskStreamEvent {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'type' in payload &&
    'data' in payload
  );
}

// ============================================================================
// Message Factories
// ============================================================================

let messageIdCounter = 0;

export function createMessage<T>(type: MessageType, payload: T): WorkerMessage<T> {
  return {
    id: `msg-${Date.now()}-${++messageIdCounter}`,
    version: '1.0',
    type,
    payload,
    timestamp: Date.now(),
  };
}

export function createResponse<T>(
  requestId: string,
  payload: T,
  duration?: number
): WorkerResponse<T> {
  return {
    id: requestId,
    version: '1.0',
    type: 'response',
    payload,
    timestamp: Date.now(),
    duration,
  };
}

export function createErrorResponse(
  requestId: string,
  error: WorkerError
): WorkerResponse<WorkerError> {
  return {
    id: requestId,
    version: '1.0',
    type: 'error',
    payload: error,
    timestamp: Date.now(),
  };
}

export function createEventResponse<T>(payload: T): WorkerResponse<T> {
  return {
    id: `evt-${Date.now()}`,
    version: '1.0',
    type: 'event',
    payload,
    timestamp: Date.now(),
  };
}

// ============================================================================
// Error Factories
// ============================================================================

export function createNetworkError(message: string, originalError?: Error): WorkerError {
  return {
    code: 'NETWORK_ERROR',
    message,
    retryable: true,
    originalError: originalError?.message,
  };
}

export function createApiError(
  status: number,
  message: string,
  details?: unknown
): WorkerError {
  return {
    code: 'API_ERROR',
    message: `HTTP ${status}: ${message}`,
    details,
    retryable: status >= 500,
  };
}

export function createTimeoutError(timeoutMs: number): WorkerError {
  return {
    code: 'TIMEOUT',
    message: `Request timed out after ${timeoutMs}ms`,
    retryable: true,
  };
}

export function createWebSocketError(message: string): WorkerError {
  return {
    code: 'WEBSOCKET_ERROR',
    message,
    retryable: true,
  };
}
